import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import PageSEO from "@/components/feature/PageSEO";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FilterBar from "@/components/feature/FilterBar";
import MovieGrid from "@/components/feature/MovieGrid";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import SectionHeading from "@/components/feature/SectionHeading";
import type { FilterState } from "@/utils/movie";
import { DEFAULT_FILTERS, filterAndSort, getHot, searchMovies } from "@/utils/movie";
import MovieListItem from "@/pages/search/components/MovieListItem";

const PAGE_SIZE = 12;
const TYPE_OPTIONS = [
  { value: "all", label: "全部" },
  { value: "movie", label: "电影" },
  { value: "tv", label: "电视剧" },
];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") || "";
  const [input, setInput] = useState(q);
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const [type, setType] = useState("all");
  const [view, setView] = useState<"grid" | "list">("grid");
  const [page, setPage] = useState(1);

  useEffect(() => {
    setInput(q);
    setPage(1);
  }, [q]);

  useEffect(() => {
    setPage(1);
  }, [filters.region, filters.genre, filters.year, filters.sort, type]);

  const result = useMemo(() => {
    const matched = searchMovies(q);
    const byType = type === "all" ? matched : matched.filter((m) => m.type === type);
    return filterAndSort(byType, filters);
  }, [q, type, filters]);

  const hot = useMemo(() => getHot(5), []);
  const totalPages = Math.max(1, Math.ceil(result.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = result.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const value = input.trim();
    setSearchParams(value ? { q: value } : {}, { replace: false });
  };

  const updateFilter = (key: keyof FilterState, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setType("all");
  };

  return (
    <Layout>
      <PageSEO
        title={q ? `“${q}” 的搜索结果 - 熊猫影院` : "影视搜索 - 熊猫影院"}
        description={
          q
            ? `熊猫影院为你找到与“${q}”相关的影视作品，支持按地区、题材、年份与热度筛选排序。`
            : "在熊猫影院搜索电影、电视剧，快速查找你想看的影视作品，支持多维筛选与排序。"
        }
        keywords={`熊猫影院,影视搜索,${q || "电影搜索"},在线观看`}
        path="/search"
      />

      <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />

        <header className="mt-6">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            影视搜索
          </h1>
          <form onSubmit={submit} className="mt-5 max-w-2xl">
            <div className="flex h-12 items-center gap-3 rounded-md border border-background-300/70 bg-background-100 px-4 focus-within:border-primary-400">
              <span className="flex h-5 w-5 items-center justify-center text-foreground-500">
                <i className="ri-search-line text-lg"></i>
              </span>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                type="text"
                name="q"
                placeholder="输入影视名称，例如：星际回响"
                className="h-full w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              <button
                type="submit"
                className="flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                搜索
              </button>
            </div>
          </form>

          {q && (
            <p className="mt-5 text-sm text-foreground-600">
              关键词 <strong className="text-foreground-900">“{q}”</strong> 共找到{" "}
              <strong className="text-primary-600">{result.length}</strong> 个结果
            </p>
          )}
        </header>

        {q ? (
          <>
            <div className="mt-6">
              <FilterBar
                typeOptions={TYPE_OPTIONS}
                currentType={type}
                onTypeChange={setType}
                filters={filters}
                onFilterChange={updateFilter}
                onReset={resetFilters}
                extraSlot={
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-foreground-500">视图</span>
                    <div className="flex rounded-full border border-background-300/70 p-1">
                      <button
                        type="button"
                        aria-label="网格视图"
                        onClick={() => setView("grid")}
                        className={`flex h-7 w-7 items-center justify-center rounded-full cursor-pointer ${
                          view === "grid"
                            ? "bg-primary-500 text-background-50"
                            : "text-foreground-600"
                        }`}
                      >
                        <i className="ri-grid-fill text-sm"></i>
                      </button>
                      <button
                        type="button"
                        aria-label="列表视图"
                        onClick={() => setView("list")}
                        className={`flex h-7 w-7 items-center justify-center rounded-full cursor-pointer ${
                          view === "list"
                            ? "bg-primary-500 text-background-50"
                            : "text-foreground-600"
                        }`}
                      >
                        <i className="ri-list-check-2 text-sm"></i>
                      </button>
                    </div>
                  </div>
                }
              />
            </div>

            <div className="mt-8">
              {pageItems.length > 0 ? (
                view === "grid" ? (
                  <MovieGrid movies={pageItems} columns={5} />
                ) : (
                  <div className="flex flex-col gap-4">
                    {pageItems.map((movie) => (
                      <MovieListItem key={movie.id} movie={movie} />
                    ))}
                  </div>
                )
              ) : (
                <EmptyState
                  title={`没有找到与“${q}”相关的作品`}
                  description="换个关键词试试，或调整下方的筛选条件，也可以看看大家正在追的热门作品。"
                  actionLabel="重置筛选条件"
                  onAction={resetFilters}
                />
              )}
            </div>

            <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
          </>
        ) : (
          <div className="mt-8">
            <EmptyState
              title="输入关键词开始搜索"
              description="支持按影视名称、类型或标签模糊搜索，例如输入“星际”“悬疑”“古装”试试。"
            />
          </div>
        )}

        <section className="mt-14">
          <SectionHeading
            title="热门推荐"
            icon="ri-fire-line"
            subtitle="没有找到想看的内容？不妨从这些高人气作品开始"
            moreTo="/category/movie"
          />
          <MovieGrid movies={hot} columns={5} />
        </section>
      </div>
    </Layout>
  );
}